import * as React from "react";
import {
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Stack,
  Tooltip,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { useSessionStorage } from "../../hooks/useSessionStorage";

export default function EditMergeFields({
  messageRef,
  value,
  setValue,
  maxChars = 320,
  onTooLong,
}) {
  const [open, setOpen] = React.useState(false);
  const [fields, setFields] = React.useState([]); // [{ key, value }]
  const [draft, setDraft] = React.useState([]);  // [{ _id, key, value }]
  const [saving, setSaving] = React.useState(false);
  const [deletingKey, setDeletingKey] = React.useState(null);
  const [user] = useSessionStorage("user", null);

  // NOTE: function.json route ends with '/', so keep it here too
  const url = "/api/MessageHub/GetMessageHubMergeFields/";

  // --- stable id factory for draft rows ---
  const newId = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `mf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  // --- insert value at cursor (or append) ---
  const insertText = React.useCallback(
    (textToInsert) => {
      const el = messageRef?.current;
      const text = String(value || "");
      const tok = String(textToInsert || "");

      if (!el || typeof el.selectionStart !== "number") {
        const next = text + tok;
        if (next.length > maxChars) return onTooLong?.(`Message too long to insert ${tok}`);
        setValue(next);
        return;
      }

      const start = el.selectionStart;
      const end = el.selectionEnd ?? start;
      const next = text.slice(0, start) + tok + text.slice(end);
      if (next.length > maxChars) return onTooLong?.(`Message too long to insert ${tok}`);
      setValue(next);

      requestAnimationFrame(() => {
        try {
          el.focus();
          el.setSelectionRange(start + tok.length, start + tok.length);
        } catch {}
      });
    },
    [messageRef, value, setValue, maxChars, onTooLong]
  );

  // --- load user's fields (GET) ---
  const fetchFields = React.useCallback(async () => {
    try {
      const res = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          user: user?.userDetails ?? "",
        },
      });
      if (!res.ok) throw new Error(`GET ${url} ${res.status}`);
      const data = await res.json();
      const cleaned = Array.isArray(data)
        ? data
            .map(({ key, value }) => ({
              key: String(key || "").trim(),
              value: String(value || "").trim(),
            }))
            .filter((r) => r.key.length > 0)
        : [];
      setFields(dedupeByKey(cleaned));
    } catch (e) {
      console.error("Failed to load merge fields:", e);
      setFields([]);
    }
  }, [url, user?.userDetails]);

  React.useEffect(() => {
    if (user?.userDetails) fetchFields();
  }, [fetchFields, user?.userDetails]);

  // --- modal controls ---
  const openModal = () => {
    // Start with current fields; if none, seed a blank row so you can type immediately
    const base = fields.length ? fields : [{ key: "", value: "" }];
    setDraft(base.map((r) => ({ _id: newId(), ...r })));
    setOpen(true);
  };
  const closeModal = () => setOpen(false);

  const handleAddRow = () =>
    setDraft((prev) => [...prev, { _id: newId(), key: "", value: "" }]);

  const handleChangeRow = (idx, prop, val) =>
    setDraft((prev) => prev.map((row, i) => (i === idx ? { ...row, [prop]: val } : row)));

  const handleDeleteField = async (key, _id) => {
    // If it's a new/blank row (no key), just remove it locally
    if (!key) {
      setDraft((prev) => prev.filter((r) => r._id !== _id));
      return;
    }
    try {
      setDeletingKey(key);
      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          user: user?.userDetails ?? "",
        },
        body: JSON.stringify({ key, user: user?.userDetails ?? "" }),
      });
      if (res.status !== 204 && !res.ok) throw new Error(`DELETE ${url} ${res.status}`);

      // update UI immediately
      setDraft((prev) => prev.filter((r) => r._id !== _id));
      setFields((prev) => prev.filter((r) => r.key !== key));
    } catch (e) {
      console.error("Failed to delete merge field:", e);
    } finally {
      setDeletingKey(null);
    }
  };

  // --- save (POST upsert per item) ---
  const handleSave = async () => {
    // No visible validation; just skip empty rows to avoid server errors
    const cleaned = dedupeByKey(
      draft
        .map(({ _id, ...r }) => ({
          key: (r.key || "").trim(),
          value: (r.value || "").trim(),
        }))
        .filter((r) => r.key.length > 0 && r.value.length > 0)
    );

    try {
      setSaving(true);
      await Promise.all(
        cleaned.map(async (item) => {
          const res = await fetch(url, {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              user: user?.userDetails ?? "",
            },
            body: JSON.stringify({ ...item, user: user?.userDetails ?? "" }),
          });
          if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(`POST ${url} ${res.status}: ${text}`);
          }
        })
      );

      setOpen(false);
      await fetchFields(); // refresh from server
    } catch (e) {
      console.error("Failed to save merge fields:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Sleek, compact chips (click inserts VALUE), plus circular "+" */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          py: 0.25,
          flexWrap: "wrap",
          alignItems: "center",
          rowGap: 0.5,
        }}
      >
        {fields.map((f) => (
          <Tooltip key={f.key} title={f.value || f.key} arrow>
            <Chip
              size="small"
              label={f.key}
              variant="outlined"
              onClick={() => insertText(f.value || f.key)}
              sx={{
                borderStyle: "dashed",
                fontWeight: 600,
                lineHeight: 1.1,
                borderRadius: 999,
                height: 22,
                px: 0.75,
                "&:hover": { boxShadow: 1, bgcolor: "action.hover" },
              }}
            />
          </Tooltip>
        ))}

        <Tooltip title="Manage fields" arrow>
          <IconButton
            onClick={openModal}
            size="small"
            sx={{
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: "50%",
              width: 24,
              height: 24,
              "&:hover": { boxShadow: 1, bgcolor: "action.hover" },
            }}
            aria-label="Manage merge fields"
          >
            <AddIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Editor modal */}
      <Dialog
        open={open}
        onClose={closeModal}
        fullWidth
        maxWidth="sm"
        // --- click/focus safety ---
        keepMounted
        disableEnforceFocus
        disableRestoreFocus
        hideBackdrop
        // If something on your page still sits above, bump this higher.
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            position: "fixed",
            zIndex: 20000, // higher than most app overlays/tooltips
            pointerEvents: "auto",
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 1,
            pr: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Edit my merge fields
          </Typography>
          <IconButton onClick={closeModal} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent dividers sx={{ p: 1.25 }}>
          <Stack spacing={1}>
            {draft.length === 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderStyle: "dashed",
                  textAlign: "center",
                  color: "text.secondary",
                  bgcolor: "background.default",
                }}
              >
                No fields yet. Add your first one.
              </Paper>
            )}

            {draft.map((row, idx) => (
              <Paper
                key={row._id}
                variant="outlined"
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: "background.paper",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr auto",
                  gap: 0.75,
                  alignItems: "center", // <-- centers/levels inputs and icon
                }}
              >
                <TextField
                  label="Key"
                  size="small"
                  value={row.key}
                  onChange={(ev) => handleChangeRow(idx, "key", ev.target.value)}
                  placeholder="ReplyTo"
                  inputProps={{ "aria-label": "merge key" }}
                  autoFocus={row.key === "" && row.value === ""} // focus brand new row
                  onMouseDown={(ev) => ev.stopPropagation()}
                  // keep inputs vertically tight & consistent
                  FormHelperTextProps={{ sx: { minHeight: 0, m: 0, lineHeight: 0 } }}
                />
                <TextField
                  label="Value"
                  size="small"
                  value={row.value || ""}
                  onChange={(ev) => handleChangeRow(idx, "value", ev.target.value)}
                  placeholder="+447700900123"
                  inputProps={{ "aria-label": "merge value" }}
                  onMouseDown={(ev) => ev.stopPropagation()}
                  FormHelperTextProps={{ sx: { minHeight: 0, m: 0, lineHeight: 0 } }}
                />
                <Tooltip title="Delete" arrow>
                  <span>
                    <IconButton
                      onClick={() => handleDeleteField(row.key, row._id)}
                      aria-label="delete"
                      disabled={!!row.key && deletingKey === row.key}
                      size="small"
                      sx={{ justifySelf: "end" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Paper>
            ))}
          </Stack>

          <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={handleAddRow} startIcon={<AddIcon />}>
              Add field
            </Button>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 1 }}>
          <Button onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  function dedupeByKey(arr) {
    const seen = new Set();
    return arr.filter((r) => (seen.has(r.key) ? false : (seen.add(r.key), true)));
  }
}
